// backup.ts — faithful, portable, round-trip backup of the on-device collection.
//
// This is the app-agnostic FRONT of the Oriora data-portability standard. The real work
// is in Rust (ipc.rs / db.rs): `export_backup` returns a self-describing JSON envelope
//   { "oriora_backup": { app, schema, exported_at, data: { …every row, verbatim… } } }
// and `import_backup` REPLACES the local collection with it ("this IS my data now").
//
// Unlike the CSV export (a lossy, read-only report), this file round-trips EXACTLY —
// ids, timestamps, cook history and all — so a user owns their data: keep it, move it to
// a new phone, drop it into iCloud Drive / Google Drive / Files, or restore it. The same
// module ships in every data-holding Oriora app; only the Rust payload shape differs.
//
// Destinations (Save/Share, GitHub, Google Drive) all sit on top of this one file — they
// just move the string somewhere. The device does everything; the server never sees it.

import { invoke } from "./tauri-web";

/** Generic restore result from the schema-agnostic engine (backup.rs). */
export interface ImportSummary {
  tables: number;
  rows: number;
}

/** The full collection as a pretty-printed backup-envelope JSON string. */
export function exportBackup(): Promise<string> {
  return invoke<string>("export_backup");
}

/** Replace the local collection with a backup file's contents. Throws a friendly
 *  string (surfaced to the user) when the file isn't a valid backup for this app. */
export function importBackup(json: string): Promise<ImportSummary> {
  return invoke<ImportSummary>("import_backup", { json });
}

/** Suggested filename, e.g. "speaking-coach-backup-2026-07-01.json". */
export function backupFilename(app = "speaking-coach"): string {
  const stamp = new Date().toISOString().slice(0, 10);
  return `${app}-backup-${stamp}.json`;
}
